import { useCallback, useEffect, useMemo, useState } from "react";
import { listAdsSocialPosts } from "@/lib/adsAssistantClient";

export type AdsIndexScope = "org" | "mine";

export type AdsIndexSort = "published_at_desc" | "sale_units_desc";

export const INDEX_PAGE_SIZE_OPTIONS = [5, 10] as const;
export type IndexPageSize = (typeof INDEX_PAGE_SIZE_OPTIONS)[number];

const DEFAULT_INCLUDES = {
  includeCategories: true,
  includeLatestAnalysis: true,
  includeSaleTotals: true,
  includeTags: true,
  includeMedia: true,
  /** Slice 17d: thumbnails use short-lived signed URLs (server TTL ~5m; refresh index to renew). */
  sign_media: true,
} as const;

export function useAdsAssistantIndexFilters() {
  const [indexScope, setIndexScope] = useState<AdsIndexScope>("org");
  const [indexFilterPlatform, setIndexFilterPlatform] = useState("");
  const [indexLocationId, setIndexLocationId] = useState("");
  const [indexSort, setIndexSort] = useState<AdsIndexSort>("published_at_desc");
  const [indexSearchDraft, setIndexSearchDraft] = useState("");
  const [indexSearchApplied, setIndexSearchApplied] = useState("");
  const [indexTagId, setIndexTagId] = useState("");
  const [indexGreenOnly, setIndexGreenOnly] = useState(false);
  const [indexPageSize, setIndexPageSize] = useState<IndexPageSize>(5);
  const [indexPage, setIndexPage] = useState(0);

  const indexFilterKey = useMemo(
    () =>
      JSON.stringify({
        indexScope,
        indexFilterPlatform,
        indexLocationId,
        indexSort,
        indexSearchApplied,
        indexTagId,
        indexGreenOnly,
      }),
    [indexScope, indexFilterPlatform, indexLocationId, indexSort, indexSearchApplied, indexTagId, indexGreenOnly],
  );

  useEffect(() => {
    setIndexPage(0);
  }, [indexFilterKey]);

  useEffect(() => {
    setIndexPage(0);
  }, [indexPageSize]);

  const applyIndexSearch = useCallback(() => {
    setIndexSearchApplied(indexSearchDraft.trim());
  }, [indexSearchDraft]);

  const clearIndexFilters = useCallback(() => {
    setIndexScope("org");
    setIndexFilterPlatform("");
    setIndexLocationId("");
    setIndexSort("published_at_desc");
    setIndexSearchDraft("");
    setIndexSearchApplied("");
    setIndexTagId("");
    setIndexGreenOnly(false);
    setIndexPage(0);
  }, []);

  const indexListParams = useMemo((): Parameters<typeof listAdsSocialPosts>[0] => {
    return {
      ...DEFAULT_INCLUDES,
      limit: indexPageSize,
      offset: indexPage * indexPageSize,
      ...(indexScope === "mine" ? { mine_only: true } : {}),
      ...(indexFilterPlatform.trim() ? { platform: indexFilterPlatform.trim().toLowerCase() } : {}),
      ...(indexLocationId.trim() ? { location_id: indexLocationId.trim() } : {}),
      ...(indexSort !== "published_at_desc" ? { sort: indexSort } : {}),
      ...(indexSearchApplied ? { search: indexSearchApplied } : {}),
      ...(indexTagId.trim() ? { tag_id: indexTagId.trim() } : {}),
      ...(indexGreenOnly ? { green_only: true } : {}),
    };
  }, [
    indexScope,
    indexFilterPlatform,
    indexLocationId,
    indexSort,
    indexSearchApplied,
    indexTagId,
    indexGreenOnly,
    indexPageSize,
    indexPage,
  ]);

  const hasActiveIndexFilters = useMemo(
    () =>
      indexScope === "mine" ||
      Boolean(indexFilterPlatform.trim()) ||
      Boolean(indexLocationId.trim()) ||
      indexSort !== "published_at_desc" ||
      Boolean(indexSearchApplied) ||
      Boolean(indexTagId.trim()) ||
      indexGreenOnly,
    [indexScope, indexFilterPlatform, indexLocationId, indexSort, indexSearchApplied, indexTagId, indexGreenOnly]
  );

  return {
    indexScope,
    setIndexScope,
    indexFilterPlatform,
    setIndexFilterPlatform,
    indexLocationId,
    setIndexLocationId,
    indexSort,
    setIndexSort,
    indexSearchDraft,
    setIndexSearchDraft,
    indexSearchApplied,
    applyIndexSearch,
    indexTagId,
    setIndexTagId,
    indexGreenOnly,
    setIndexGreenOnly,
    clearIndexFilters,
    indexListParams,
    hasActiveIndexFilters,
    indexPage,
    setIndexPage,
    indexPageSize,
    setIndexPageSize,
  };
}
